<script setup lang="ts">
/* Stores */
import { useCityStore } from '@/store/city/city'

/* Components */
import TheNewsList from '@/components/News/TheNewsList.vue'

/* Composables */
import { useNewsPagination } from '@/composables/useNewsPagination'

/* Types */
import { SlugNews } from '@/types/api/api-v3/news'
import {
  HttpStatusCode,
  NotFoundError,
  ServerError,
  Success,
} from '@/api/results'

/* Stores */
const {
  cityInfo: { padeg, domain },
} = useCityStore()

const newsList = ref<SlugNews[]>([])

const { $apiService } = useNuxtApp()

const {
  countNews,
  currentPage,
  countElementsOnPage,
  countPages,
  onChangeCurrentPage,
} = useNewsPagination()

const pageTitle = padeg?.pred
  ? `Новости от Farfor в ${padeg.pred}`
  : 'Новости от Farfor'

const pageDescription = padeg?.pred
  ? `Важные новости и приятные акции от доставки суши и пиццы Farfor в ${padeg.pred}: узнавайте больше, заказывайте чаще`
  : 'Важные новости и приятные акции от доставки суши и пиццы Farfor: узнавайте больше, заказывайте чаще'

const { data, error } = await useAsyncData(async () => {
  const responseNewsData = await $apiService.news.fetchListNews(
    currentPage.value,
    countElementsOnPage,
  )

  if (responseNewsData instanceof NotFoundError) {
    throw createError({
      statusCode: HttpStatusCode.NOT_FOUND,
    })
  }

  if (responseNewsData instanceof ServerError) {
    throw createError({
      statusCode: HttpStatusCode.INTERNAL_SERVER_ERROR,
    })
  }

  return responseNewsData.data
})

if (error.value) showError(error.value)

countNews.value = data?.value?.count || 1

newsList.value = data.value?.results || []

const breadcrumbs = computed(() => {
  const pred = padeg.pred

  return [
    { text: `Доставка суши в ${pred}`, href: '/' },
    { text: 'Новости', href: 'news' },
  ]
})

const fetchNewsData = async () => {
  const responseNewsData = await $apiService.news.fetchListNews(
    currentPage.value,
    countElementsOnPage,
  )

  if (responseNewsData instanceof Success) {
    newsList.value = (responseNewsData.data?.results || []) as SlugNews[]
    countNews.value = responseNewsData.data?.count || 1
  }
}
</script>

<template>
  <div class="news-page">
    <app-head
      :title="pageTitle"
      :seo="{
        title: pageTitle,
        description: pageDescription,
        keywords: '',
      }"
    />

    <the-breadcrumbs
      class="news-page__breadcrumbs"
      :breadcrumbs="breadcrumbs"
      :domain="domain"
    />

    <h1 class="news-page__title">Новости</h1>

    <the-news-list
      :current-page="currentPage"
      :count-pages="countPages"
      :news-list="newsList"
      with-image
      is-light
      @update:model-value="onChangeCurrentPage($event, fetchNewsData)"
    />
  </div>
</template>

<style lang="scss" scoped>
.news-page {
  padding-top: 40px;

  &__breadcrumbs {
    margin-bottom: 20px;
  }

  &__title {
    margin-bottom: 48px;
    font-size: 28px;
    font-weight: 800;
  }

  @include media('<desktop-mini') {
    padding-top: 20px;

    &__title {
      margin-bottom: 27px;
      font-size: 18px;
    }
  }
}
</style>
